<template>
  <div class="talent-card-btn-wrapper">
    <!-- Trigger Button -->
    <vs-tooltip text="View Talent Card" v-if="userId">
      <vs-button
        class="action-btn"
        color="success"
        type="filled"
        icon-pack="feather"
        icon="icon-user"
        size="small"
        :disabled="isLoading"
        @click.stop="openTalentCard"
      >
      </vs-button>
    </vs-tooltip>

    <!-- Popup -->
    <vs-popup class="talent-card-popup" title="" :active.sync="isPopupActive">
      
      <!-- Custom Close Icon (since we hide default header) -->
      <div class="card-close-icon" @click="isPopupActive = false">
        <feather-icon icon="XIcon" class="w-6 h-6 cursor-pointer" />
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="loading-state">
        <div class="con-vs-loading"><div class="vs-loading"></div></div>
        <p>Fetching Profile...</p>
      </div>

      <!-- Content -->
      <div v-else-if="profileData" class="card-container">
        <!-- Header Section -->
        <div class="card-header">
          <div class="avatar-wrapper">
             <!-- Match Main View Avatar Logic -->
             <vs-avatar 
               v-if="profileData.profilePictureUrl"
               size="100px"
               class="tc-avatar-component"
               :src="`https://gtbrdd-storage.s3.us-west-2.amazonaws.com/editor/profile-pictures/${profileData.profilePictureUrl}`" 
             />
             <vs-avatar 
               v-else 
               size="100px"
               class="tc-avatar-component"
               :src="`https://hostedboringavatars.vercel.app/api/beam?name=${profileData.firstName}+${profileData.lastName}&colors=F2C244,E77E23,7367F0,28C76F,EA5455`" 
             />
          </div>
        </div>

        <!-- Card Body -->
        <div class="card-body">
          <h2 class="user-name">{{ profileData.firstName }} {{ profileData.lastName }}</h2>
          <p class="user-role">{{ profileData.profession || 'Candidate' }}</p>

          <div class="divider"></div>

          <!-- CAREER OBJECTIVE -->
          <div class="info-section">
            <h5 class="section-label">CAREER OBJECTIVE</h5>
            <p class="objective-text">
              {{ profileData.careerObjective || 'No objective provided.' }}
            </p>
          </div>

          <!-- CONTACT INFO -->
          <div class="info-section contact-info">
            <h5 class="section-label">CONTACT INFO</h5>
            
            <div class="contact-row" v-if="profileData.address">
              <feather-icon icon="MapPinIcon" class="icon-small" />
              <span>{{ profileData.address }}</span>
            </div>

            <div class="contact-row" v-if="profileData.contactNumber">
              <feather-icon icon="PhoneIcon" class="icon-small" />
              <span>{{ profileData.contactNumber }}</span>
            </div>

            <div class="contact-row">
              <feather-icon icon="MailIcon" class="icon-small" />
              <span class="email-text">{{ profileData.email || email }}</span>
            </div>
          </div>

          <!-- SKILLS -->
          <div class="info-section" v-if="formattedSkills.length > 0">
            <h5 class="section-label">SKILLS</h5>
            <div class="skills-container">
              <span v-for="(skill, i) in formattedSkills" :key="i" class="skill-chip">
                {{ skill }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
const API_BASE_URL = "https://api.getboarded.tech";

export default {
  name: 'TalentCardPreview',
  props: {
    userId: { type: String, default: '' },
    email: { type: String, default: '' }
  },
  data() {
    return {
      isPopupActive: false,
      isLoading: false,
      profileData: null,
    };
  },
  computed: {
    formattedSkills() {
      if (!this.profileData) return [];
      if (this.profileData.skills && this.profileData.skills.length) {
         return this.profileData.skills.map(s => s.title || s);
      }
      if (this.profileData.extraCurricular && this.profileData.extraCurricular.length > 0) {
        const ec = this.profileData.extraCurricular[0];
        if (ec.skills && Array.isArray(ec.skills)) {
          return ec.skills.map(s => s.title || s);
        }
      }
      return [];
    }
  },
  watch: {
    userId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.profileData = null; // Clear old data to show loader for the new user
      }
    }
  },
  methods: {
    async openTalentCard() {
      if (!this.userId) return;
      this.isPopupActive = true;
      if (this.profileData) return;

      this.isLoading = true;
      try {
        const url = `${API_BASE_URL}/users/${this.userId}`;
        const token = localStorage.getItem('accessToken') || this.$store.state.bearerToken;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) throw new Error('API Error');
        this.profileData = await response.json();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.talent-card-btn-wrapper {
  display: inline-block;
}

/* Button Styling */
.action-btn {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 !important;
}

.action-btn >>> .vs-icon {
  color: #28c76f !important;
  font-size: 16px !important;
  margin: 0 !important;
}

.action-btn:hover {
  background: #28c76f !important;
  border-radius: none !important;
}

.action-btn:hover >>> .vs-icon {
  color: #ffffff !important;
}

.talent-card-popup >>> .vs-popup {
  width: 400px !important;
  background: transparent !important;
  box-shadow: none !important;
}

.talent-card-popup >>> .vs-popup--header {
  display: none !important; /* Remove the white bar header */
}

.talent-card-popup >>> .vs-popup--content {
  padding: 0 !important;
  border-radius: 20px;
  overflow: hidden;
  background: white;
  position: relative;
  max-height: 80vh;           
  overflow-y: auto;           
  scrollbar-width: none;      
  -ms-overflow-style: none;  
}

.talent-card-popup >>> .vs-popup--content::-webkit-scrollbar {
  display: none;
}

.card-close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 100;
  color: #626262;
  background: rgba(255,255,255,0.4);
  border-radius: 50%;
  padding: 4px;
}

/* CARD DESIGN */
.card-container {
  display: flex;
  flex-direction: column;
}

.card-header {
  height: 100px;
  background: #fdfdfd;
  display: flex;
  justify-content: center;
  position: relative;
}

.avatar-wrapper {
  position: absolute;
  bottom: -50px;
  z-index: 10;
}

.tc-avatar-component {
  border: 5px solid white !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.card-body {
  background: linear-gradient(135deg, #7367F0 0%, #9E95F5 100%);
  color: white;
  padding: 65px 30px 40px 30px;
  text-align: left;
}

.user-name {
  text-align: center;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: white;
}

.user-role {
  text-align: center;
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 4px;
  margin-bottom: 25px;
}

.divider {
  height: 1px;
  background: rgba(255,255,255,0.25);
  margin-bottom: 25px;
}

.info-section {
  margin-bottom: 25px;
}

.section-label {
  font-size: 0.75rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  font-weight: 600;
  margin-bottom: 10px;
}

.objective-text {
  font-size: 0.9rem;
  line-height: 1.6;
}

.contact-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  font-size: 0.9rem;
}

.icon-small {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  opacity: 0.8;
}

.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.skill-chip {
  background: rgba(255,255,255,0.25);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
}

.loading-state {
  padding: 60px;
  text-align: center;
  color: #7367F0;
}
</style>